import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Block } from '../../../../components/UI/Contents'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import styled from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'

import {
  TextError,
  ErrorContainer,
  LastButtonsContainer,
  TextUploadSizeRecomended,
  ChangeLogo,
  InfoContainer,
  CheckboxContainer,
} from './styled'
import { Button } from '../../../../components/UI/Buttons'
import Input from 'views/components/UI/TextInput'
import Select from 'views/components/UI/Select'
import { H2, H3, P } from '../../../../components/UI/Text'
import RadioCheck from 'views/components/UI/InputRadio'
import ImagePicker from 'views/components/ImagePicker'
import Switch from '@mui/material/Switch'

import { generalDataSelector } from 'ducks/auth'
import { IIntegration } from 'services/companies/getIntegrations/getIntegrations'

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

interface IRow {
  margin?: string
  width?: string
  justifyContent?: string
  alignItems?: string
  flexDirection?: string
  gap?: string
}

const Row = styled.div<IRow>`
  display: flex;
  flex-direction: ${(props) => props.flexDirection || 'row'};
  justify-content: ${(props) => props.justifyContent || 'space-between'};
  align-items: ${(props) => props.alignItems || 'flex-start'};
  width: ${(props) => props.width || '100%'};
  margin-top: 10px;
  margin: ${(props) => props.margin || 'none'};
  gap: ${(props) => props.gap || 'none'};
`

const SignUpSchema = Yup.object().shape({
  name: Yup.string().required('El nombre de la empresa es obligatorio'),
  operativeId: Yup.string().required('El nombre del operativo es obligatorio'),
  contactName: Yup.string().required('El nombre del contacto es obligatorio'),
  contactEmail: Yup.string().email('El email no es válido').required('El email es obligatorio'),
  phone: Yup.string()
    .required('El teléfono es obligatorio')
    .min(7, 'El teléfono debe tener al menos 7 caracteres')
    .max(16, 'El teléfono debe tener máximo 16 caracteres'),
  commercialInCharge: Yup.string().required('El comercial encargado es obligatorio'),
  companyCategory: Yup.string().required('La categoría de la empresa es obligatoria'),
  typeCompanyId: Yup.string().required('El carácter es obligatorio'),
  hasIntegration: Yup.string().required('La integración es obligatoria'),
  integrationType: Yup.number().when('hasIntegration', {
    is: (value: boolean | string) => value === true || value === 'true',
    then: Yup.number().required('El tipo de integración es obligatorio'),
  }),
  workModel: Yup.string().required('El modelo de trabajo es obligatorio'),
  industryType: Yup.string().required('El tipo de industria es obligatorio'),
  marketComplexity: Yup.string().required('La complejidad del mercado es obligatoria'),
  lifeCycle: Yup.string().required('El ciclo de vida de la empresa es obligatorio'),
  maturityLevel: Yup.string().required('La madurez del área de talento humano es obligatoria'),
})

type ISubmit = {
  name: string
  typeCompanyId: number
  operativeId: number
  contactName: string
  contactEmail: string
  phone: string
  companyCharacter: string
  logo?: Blob
  hasIntegration: string
  integrationType?: number
  commercialInCharge: string
  companyCategory: number
  workModel: string
  industryType: string
  marketComplexity: string
  lifeCycle: string
  maturityLevel: string
  usesAi: boolean
  hasQuestionnaireManagement: string
  mfaEnabled: string
}

interface IProps {
  onSubmit: (values: ISubmit) => void
  integrations: IIntegration[]
}

// MAIN
/**
 * @description Componente formulario de registro
 */
const RegisterForm = (props: IProps) => {
  const navigate = useNavigate()
  const { onSubmit, integrations } = props
  const { data } = useSelector(generalDataSelector)
  const [imageCompany, setImageCompany] = useState<string | null>(null)
  const [imageBlob, setImageBlob] = useState<Blob | null>(null)
  const [companyCharacter, setCompanyCharacter] = useState<string | undefined>()
  const [companyCategory, setCompanyCategory] = useState<string | undefined>()
  const [hasIntegration, setHasIntegration] = useState<string | undefined>('false')
  const [hasQuestionnaireManagement, setHasQuestionnaireManagement] = useState<
    string | undefined
  >('false')
  const [mfaEnabled, setMfaEnabled] = useState<string | undefined>('false')

  const associatedOperating = data?.list_admin.map((admin: any) => {
    return {
      value: admin.id,
      label: `${admin.first_name} ${admin.last_name}`,
    }
  })

  const maturityLevels = data?.company_hr_maturity_level.map((level: any) => {
    return {
      value: level.id,
      label: level.name,
    }
  })

  const industryTypes = data?.company_industry_type.map((type: any) => {
    return {
      value: type.id,
      label: type.name,
    }
  })

  const lifeCycles = data?.company_life_cycle.map((cycle: any) => {
    return {
      value: cycle.id,
      label: cycle.name,
    }
  })

  const marketComplexity = data?.company_market_complexity.map((complexity: any) => {
    return {
      value: complexity.id,
      label: complexity.name,
    }
  })

  const handleImage = (e: any) => {
    const file = e.target.files[0]
    setImageBlob(file)

    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onloadend = () => {
      setImageCompany(reader.result as string)
    }
  }

  const isDisabled = (allErrors: any) => {
    return Object.keys(allErrors).length > 0
  }

  return (
    <Container>
      <Formik
        validateOnChange={true}
        validateOnMount={true}
        validationSchema={SignUpSchema}
        onSubmit={(values) => {
          onSubmit({
            name: values.name,
            typeCompanyId: parseInt(values.typeCompanyId),
            operativeId: parseInt(values.operativeId),
            contactName: values.contactName,
            contactEmail: values.contactEmail,
            phone: values.phone,
            commercialInCharge: values.commercialInCharge,
            companyCategory: parseInt(values.companyCategory),
            companyCharacter: companyCharacter || '',
            logo: imageBlob ? imageBlob : undefined,
            hasIntegration: values.hasIntegration,
            integrationType: values.integrationType ?? undefined,
            workModel: values.workModel,
            industryType: values.industryType,
            marketComplexity: values.marketComplexity,
            lifeCycle: values.lifeCycle,
            maturityLevel: values.maturityLevel,
            usesAi: values.usesAi,
            hasQuestionnaireManagement: values.hasQuestionnaireManagement,
            mfaEnabled: values.mfaEnabled,
          })
        }}
        initialValues={{
          name: '',
          typeCompanyId: '',
          operativeId: '',
          contactName: '',
          contactEmail: '',
          phone: '',
          commercialInCharge: '',
          companyCategory: '',
          companyCharacter: '',
          hasIntegration: 'false',
          integrationType: undefined,
          workModel: '',
          industryType: '',
          marketComplexity: '',
          lifeCycle: '',
          maturityLevel: '',
          usesAi: false,
          hasQuestionnaireManagement: 'false',
          mfaEnabled: 'false',
        }}
      >
        {({ errors, touched, values, setFieldValue, handleChange, handleBlur, handleSubmit }) => {
          return (
            <Form style={{ width: '100%' }}>
              <Block display="flex" flexDirection="column">
                <Row width="100%" gap="20px">
                  <Row width="48%" flexDirection="column">
                    <Input
                      name="name"
                      type="text"
                      label="Nombre de la empresa"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                    />
                    {errors.name && touched.name ? (
                      <ErrorContainer>
                        <TextError>{errors.name}</TextError>
                      </ErrorContainer>
                    ) : null}
                  </Row>
                  <Row width="48%" flexDirection="column">
                    <Select
                      width="98%"
                      hideNeverChoice={true}
                      label="Operativo asociado"
                      options={associatedOperating}
                      onChange={(e) => {
                        handleChange('operativeId')(e.toString())
                      }}
                      value={values.operativeId}
                    />
                    {errors.operativeId && touched.operativeId ? (
                      <ErrorContainer>
                        <TextError>{errors.operativeId}</TextError>
                      </ErrorContainer>
                    ) : null}
                  </Row>
                  <Row width="48%" flexDirection="column">
                    <Input
                      name="contactName"
                      type="text"
                      label="Nombre de contacto"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.contactName}
                    />
                    {errors.contactName && touched.contactName ? (
                      <ErrorContainer>
                        <TextError>{errors.contactName}</TextError>
                      </ErrorContainer>
                    ) : null}
                  </Row>
                </Row>
                <Row width="100%" gap="20px">
                  <Row width="48%" flexDirection="column">
                    <Input
                      name="contactEmail"
                      type="text"
                      label="Email de contacto"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.contactEmail}
                    />
                    {errors.contactEmail && touched.contactEmail ? (
                      <ErrorContainer>
                        <TextError>{errors.contactEmail}</TextError>
                      </ErrorContainer>
                    ) : null}
                  </Row>
                  <Row width="48%" flexDirection="column">
                    <Input
                      name="phone"
                      type="number"
                      label="Teléfono de contacto"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone}
                    />
                    {errors.phone && touched.phone ? (
                      <ErrorContainer>
                        <TextError>{errors.phone}</TextError>
                      </ErrorContainer>
                    ) : null}
                  </Row>
                  <Row width="48%" flexDirection="column">
                    <Input
                      name="commercialInCharge"
                      type="text"
                      label="Comercial encargado"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.commercialInCharge}
                    />
                    {errors.commercialInCharge && touched.commercialInCharge ? (
                      <ErrorContainer>
                        <TextError>{errors.commercialInCharge}</TextError>
                      </ErrorContainer>
                    ) : null}
                  </Row>
                </Row>
                <Row flexDirection="column" margin="16px 0 10px 0">
                  <Select
                    width="32%"
                    hideNeverChoice={true}
                    label="Tipo de industria de la compañía"
                    options={industryTypes}
                    onChange={(e) => {
                      handleChange('industryType')(e.toString())
                    }}
                    value={values.industryType}
                  />
                  {errors.industryType && touched.industryType ? (
                    <ErrorContainer>
                      <TextError>{errors.industryType}</TextError>
                    </ErrorContainer>
                  ) : null}
                </Row>
                <Row width="100%" alignItems="center" justifyContent="left" gap="10px">
                  <P fontSize="18px" weight={600} color="#1F2124">
                    Con IA
                  </P>
                  <Switch
                    checked={values.usesAi}
                    onChange={(e) => {
                      setFieldValue('usesAi', e.target.checked)
                    }}
                    aria-label="Con IA"
                  />
                </Row>
                <InfoContainer>
                  <H2>Características organizacionales</H2>
                  <Row justifyContent="none" gap="20px">
                    <Row width="48%" flexDirection="column">
                      <Row gap="20px">
                        <Row flexDirection="column" margin="0 0 0 0">
                          <Select
                            hideNeverChoice={true}
                            label="Complejidad del mercado"
                            options={marketComplexity}
                            onChange={(e) => {
                              handleChange('marketComplexity')(e.toString())
                            }}
                            value={values.marketComplexity}
                          />
                          {errors.marketComplexity && touched.marketComplexity ? (
                            <ErrorContainer>
                              <TextError>{errors.marketComplexity}</TextError>
                            </ErrorContainer>
                          ) : null}
                        </Row>
                        <Row flexDirection="column" margin="0 0 0 0">
                          <Select
                            hideNeverChoice={true}
                            label="Ciclo de vida de la empresa"
                            options={lifeCycles}
                            onChange={(e) => {
                              handleChange('lifeCycle')(e.toString())
                            }}
                            value={values.lifeCycle}
                          />
                          {errors.lifeCycle && touched.lifeCycle ? (
                            <ErrorContainer>
                              <TextError>{errors.lifeCycle}</TextError>
                            </ErrorContainer>
                          ) : null}
                        </Row>
                      </Row>
                      <Row flexDirection="column" margin="20px 0 0 0">
                        <Select
                          width="100%"
                          hideNeverChoice={true}
                          label="Madurez del área de talento humano"
                          options={maturityLevels}
                          onChange={(e) => {
                            handleChange('maturityLevel')(e.toString())
                          }}
                          value={values.maturityLevel}
                        />
                        {errors.maturityLevel && touched.maturityLevel ? (
                          <ErrorContainer>
                            <TextError>{errors.maturityLevel}</TextError>
                          </ErrorContainer>
                        ) : null}
                      </Row>
                    </Row>
                    <Row width="40%" flexDirection="column" margin="20px 0 0 0">
                      <Input
                        name="workModel"
                        type="textarea"
                        label="Modelo de trabajo"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.workModel}
                        style={{ height: '133px' }}
                        minRows={6}
                        maxRows={5}
                      />

                      {errors.workModel && touched.workModel ? (
                        <ErrorContainer>
                          <TextError>{errors.workModel}</TextError>
                        </ErrorContainer>
                      ) : null}
                    </Row>
                  </Row>
                </InfoContainer>

                <Row width="100%" margin="0 0 0 20px">
                  <Row width="100%" flexDirection="column" justifyContent="flex-end">
                    <P weight={600} color="#1F2124">
                      Logo de la empresa
                    </P>
                    {imageCompany === null ? (
                      <Row width="100%" alignItems="center" justifyContent="flex-start">
                        <ImagePicker onChange={handleImage} value={imageCompany ? imageCompany : ''} />
                        <TextUploadSizeRecomended>Tamaño recomendado: 200x200px</TextUploadSizeRecomended>
                      </Row>
                    ) : (
                      <>
                        <img
                          src={imageCompany}
                          alt="logo"
                          style={{ width: '200px', height: '200px', objectFit: 'contain' }}
                        />
                        <ChangeLogo onClick={() => setImageCompany(null)}>
                          <p style={{ color: '#00A6FF' }}>Cambiar imagen</p>
                          <LoopIcon style={{ color: '#00A6FF' }} />
                        </ChangeLogo>
                      </>
                    )}
                  </Row>
                </Row>
                <Row flexDirection="column" width="100%" margin="30px 0px 0px 20px">
                  <CheckboxContainer>
                    <H3 color="#1F2124" margin="0px 10px 10px 0px">
                      Categoría:
                    </H3>
                    <Row gap="10px" justify-content="flex-start" width="auto" margin="0px">
                      <RadioCheck
                        label="Demo"
                        onChange={() => {
                          setCompanyCategory('demo')
                          handleChange('companyCategory')('1')
                        }}
                        value={companyCategory}
                        checked={companyCategory === 'demo'}
                      />

                      <Row justify-content="flex-start" width="auto" margin="0px">
                        <RadioCheck
                          label="Suscripción"
                          onChange={() => {
                            setCompanyCategory('suscripción')
                            handleChange('companyCategory')('2')
                          }}
                          value={companyCategory}
                          checked={companyCategory === 'suscripción'}
                        />
                      </Row>
                      <Row justify-content="flex-start" width="auto" margin="0px">
                        <RadioCheck
                          label="Proyecto"
                          onChange={() => {
                            setCompanyCategory('proyecto')
                            handleChange('companyCategory')('3')
                          }}
                          value={companyCategory}
                          checked={companyCategory === 'proyecto'}
                        />
                      </Row>
                    </Row>
                  </CheckboxContainer>
                  {errors.companyCategory && touched.companyCategory ? (
                    <ErrorContainer>
                      <TextError>{errors.companyCategory}</TextError>
                    </ErrorContainer>
                  ) : null}
                  <CheckboxContainer>
                    <H3 color="#1F2124" margin="0px 10px 10px 0px">
                      ¿Cuál es el carácter de esta empresa?
                    </H3>
                    <Row gap="10px" justifyContent="flex-start" width="auto" margin="0px">
                      <RadioCheck
                        label="Corporativo"
                        onChange={() => {
                          setCompanyCharacter('corporate')
                          handleChange('typeCompanyId')('1')
                        }}
                        value={companyCharacter}
                        checked={companyCharacter === 'corporate'}
                      />
                      <RadioCheck
                        label="Vocacional"
                        onChange={() => {
                          setCompanyCharacter('vocational')
                          handleChange('typeCompanyId')('2')
                        }}
                        value={companyCharacter}
                        checked={companyCharacter === 'vocational'}
                      />
                    </Row>
                  </CheckboxContainer>
                  {errors.typeCompanyId && touched.typeCompanyId ? (
                    <ErrorContainer>
                      <TextError>{errors.typeCompanyId}</TextError>
                    </ErrorContainer>
                  ) : null}
                  <CheckboxContainer>
                    <H3 color="#1F2124" margin="0px 10px 10px 0px">
                      ¿Tiene integración?
                    </H3>
                    <Row gap="10px" justifyContent="flex-start" width="auto" margin="0px">
                      <RadioCheck
                        label="Si"
                        onChange={() => {
                          setHasIntegration('true')
                          handleChange('hasIntegration')('true')
                        }}
                        value={hasIntegration}
                        checked={hasIntegration === 'true'}
                      />

                      <Row justify-content=" flex-start" width="auto" margin="0px">
                        <RadioCheck
                          label="No"
                          onChange={() => {
                            setHasIntegration('false')
                            handleChange('hasIntegration')('false')
                          }}
                          value={hasIntegration}
                          checked={hasIntegration === 'false'}
                        />
                      </Row>
                    </Row>
                  </CheckboxContainer>
                  {hasIntegration === 'true' && (
                    <>
                      <CheckboxContainer>
                        <H3 color="#1F2124" margin="0px 10px 10px 0px">
                          Tipo de integración
                        </H3>
                        <Row gap="10px" justifyContent="flex-start" width="auto" margin="0px">
                          {integrations?.length &&
                            integrations.map((item, index) => (
                              <RadioCheck
                                key={`integration-${index}`}
                                label={item.name}
                                onChange={() => {
                                  setFieldValue('integrationType', item.id)
                                }}
                                value={item.id.toString()}
                                checked={values.integrationType === item.id}
                              />
                            ))}
                        </Row>
                      </CheckboxContainer>
                      {errors.integrationType && touched.integrationType ? (
                        <ErrorContainer>
                          <TextError>{errors.integrationType}</TextError>
                        </ErrorContainer>
                      ) : null}
                    </>
                  )}
                  <CheckboxContainer>
                    <H3 color="#1F2124" margin="0px 10px 10px 0px">
                      ¿Crear cuestionario?
                    </H3>
                    <Row gap="10px" justifyContent="flex-start" width="auto" margin="0px">
                      <RadioCheck
                        label="Habilitado"
                        onChange={() => {
                          setHasQuestionnaireManagement('true')
                          handleChange('hasQuestionnaireManagement')('true')
                        }}
                        value={hasQuestionnaireManagement}
                        checked={hasQuestionnaireManagement === 'true'}
                      />
                      <Row justify-content="flex-start" width="auto" margin="0px">
                        <RadioCheck
                          label="Deshabilitado"
                          onChange={() => {
                            setHasQuestionnaireManagement('false')
                            handleChange('hasQuestionnaireManagement')('false')
                          }}
                          value={hasQuestionnaireManagement}
                          checked={hasQuestionnaireManagement === 'false'}
                        />
                      </Row>
                    </Row>
                  </CheckboxContainer>
                  <CheckboxContainer>
                    <H3 color="#1F2124" margin="0px 10px 10px 0px">
                      ¿Activar MFA?
                    </H3>
                    <Row gap="10px" justifyContent="flex-start" width="auto" margin="0px">
                      <RadioCheck
                        label="Habilitado"
                        onChange={() => {
                          setMfaEnabled('true')
                          handleChange('mfaEnabled')('true')
                        }}
                        value={mfaEnabled}
                        checked={mfaEnabled === 'true'}
                      />
                      <Row justify-content="flex-start" width="auto" margin="0px">
                        <RadioCheck
                          label="Deshabilitado"
                          onChange={() => {
                            setMfaEnabled('false')
                            handleChange('mfaEnabled')('false')
                          }}
                          value={mfaEnabled}
                          checked={mfaEnabled === 'false'}
                        />
                      </Row>
                    </Row>
                  </CheckboxContainer>
                </Row>
              </Block>
              <LastButtonsContainer>
                <P
                  onClick={() => {
                    navigate(-1)
                  }}
                  style={{ cursor: 'pointer' }}
                  weight={700}
                  color="#2D749C"
                >
                  Cancelar
                </P>
                <Button disabled={isDisabled(errors)} type="submit" onClick={handleSubmit}>
                  Guardar
                </Button>
              </LastButtonsContainer>
            </Form>
          )
        }}
      </Formik>
    </Container>
  )
}

export default RegisterForm
